import { MimeEnum } from "../commonEnums";
import React, { useEffect, useRef, useState } from "react";

const UnknownFile = "unknown";
type TFileType = MimeEnum | string;
type DraggedFileType = TFileType | null;

interface IReturnType {
  draggedFileType: DraggedFileType;
  isDraggedOver: boolean;
  isFileUnknown: boolean;
  isFileValid: boolean;
  isFileInvalid: boolean;
  eventHandlers: {
    onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave: () => void;
    onDragEnd: () => void;
    onDrop: () => void;
  };
}

export const useFileDragAndDrop = (
  acceptedFiles: TFileType[] = []
): IReturnType => {
  const counter = useRef<number>(0);
  const [draggedFileType, setDraggedFileType] = useState<DraggedFileType>(null);

  useEffect(() => {
    const dragHandler = (event: DragEvent) => {
      const element = event.target as HTMLInputElement;

      if (
        element.tagName?.toLowerCase() !== "input" ||
        element?.type !== "file"
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener("dragover", dragHandler);
    window.addEventListener("drop", dragHandler);

    return () => {
      window.removeEventListener("dragover", dragHandler);
      window.removeEventListener("drop", dragHandler);
    };
  }, []);

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    const draggedItem = event.dataTransfer?.items?.[0];
    counter.current++;

    setDraggedFileType(draggedItem?.type || UnknownFile);
  };

  const onDragLeave = () => {
    counter.current--;

    if (counter.current <= 0) {
      setDraggedFileType(null);
    }
  };

  const onDragEnd = () => {
    counter.current = 0;
    setDraggedFileType(null);
  };

  return {
    draggedFileType,
    isDraggedOver: !!draggedFileType,
    isFileValid: !!draggedFileType && acceptedFiles.includes(draggedFileType),
    isFileInvalid:
      !!draggedFileType && !acceptedFiles.includes(draggedFileType),
    isFileUnknown: draggedFileType === UnknownFile,
    eventHandlers: {
      onDragEnter,
      onDragLeave,
      onDragEnd,
      onDrop: onDragEnd,
    },
  };
};
